import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Activity, Loader2, RefreshCcw, TrendingDown, TrendingUp, Minus } from "lucide-react";

export default function A1cReportPage() {
  const [loading, setLoading] = useState(false);
  const [job, setJob] = useState(null);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(365);
  const pollTimer = useRef(null);

  const poll = async () => {
    try {
      const { data: j } = await api.get("/drchrono/a1c-report-status");
      setJob(j);
      if (j.phase === "done") {
        setData(j.result);
        setLoading(false);
        const withData = (j.result?.patients || []).filter(p => p.readings?.length > 0).length;
        if (withData === 0) toast.warning("No A1C readings found. Not stored as structured vitals or in the /clinical_notes text we synced.", { duration: 10000 });
        else toast.success(`Loaded A1C data for ${withData} of ${j.result.patients.length} patients`);
        return;
      }
      if (j.phase === "error") {
        setLoading(false);
        toast.error(`A1C report errored: ${j.error || "unknown"}`);
        return;
      }
      pollTimer.current = setTimeout(poll, 5000);
    } catch (e) {
      setLoading(false);
      toast.error("Polling failed — try again");
    }
  };

  const run = async () => {
    setLoading(true); setData(null); setJob(null);
    try {
      const { data: j } = await api.post("/drchrono/a1c-report", null, { params: { limit: 5, days } });
      setJob(j);
      toast.info("Fetching from DrChrono in background — takes 1-3 min for 5 patients.", { duration: 6000 });
      pollTimer.current = setTimeout(poll, 4000);
    } catch (e) {
      setLoading(false);
      toast.error(e?.response?.data?.detail || "A1C dispatch failed");
    }
  };

  // Resume polling if a job is already running when page loads
  useEffect(() => {
    api.get("/drchrono/a1c-report-status").then(({ data: j }) => {
      if (j && ["queued", "running"].indexOf(j.phase) !== -1) {
        setJob(j); setLoading(true); pollTimer.current = setTimeout(poll, 3000);
      } else if (j?.phase === "done" && j.result) {
        setJob(j); setData(j.result);
      }
    }).catch(() => {});
    return () => { if (pollTimer.current) clearTimeout(pollTimer.current); };
  }, []); // eslint-disable-line

  return (
    <div className="p-6 md:p-10 max-w-6xl" data-testid="a1c-report-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-[#8A847A]">Clinical Outcomes</div>
          <h1 className="heading text-3xl sm:text-4xl font-bold text-[#292723] mt-2">A1C Trend Report</h1>
          <p className="text-sm text-[#5C5850] mt-1.5">Top patient by visit count from each of 5 NPs. Shows first vs latest A1C reading in the window.</p>
        </div>
        <div className="flex gap-2">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="text-sm border border-[#E5E0D8] rounded px-3 py-2 bg-white" data-testid="a1c-window-select" disabled={loading}>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
            <option value={365}>Last year</option>
            <option value={730}>Last 2 years</option>
          </select>
          <Button onClick={run} disabled={loading} className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="run-a1c-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <RefreshCcw className="h-4 w-4 mr-2"/>}
            {loading ? (job?.phase === "running" ? `Running… ${job.progress?.done || 0}/${job.progress?.total || 5}` : "Fetching…") : (data ? "Reload" : "Run report")}
          </Button>
        </div>
      </div>

      {loading && !data && (
        <div className="mt-8 bg-white border border-[#E5E0D8] rounded-md p-8 text-center text-[#8A847A]" data-testid="a1c-progress">
          <Loader2 className="h-6 w-6 mx-auto animate-spin mb-3"/>
          <div className="text-sm">Fetching live from DrChrono — background job.</div>
          {job?.progress && <div className="text-xs mt-2 font-mono">Patient {job.progress.done || 0} of {job.progress.total || 5}</div>}
          <div className="text-[11px] text-[#8A847A] mt-2 italic">Safe to leave this page — job runs on the server. Come back and refresh.</div>
        </div>
      )}

      {data && (
        <>
          <div className="mt-6 space-y-4">
            {(data.patients || []).map((p) => (
              <PatientCard key={`${p.np_id}-${p.drchrono_patient_id}`} p={p} />
            ))}
            {(data.patients || []).length === 0 && (
              <div className="bg-[#FEF7E6] border border-[#E5D9BE] rounded-md p-6 text-sm text-[#8A6E2A]">
                No patients found. This report needs synced visits with `drchrono_patient_id` — hit Sync now on Settings first.
              </div>
            )}
          </div>
          <div className="mt-6 text-xs text-[#8A847A] italic p-3 bg-[#F7F5F0] border border-[#E5E0D8] rounded">
            <b>Note:</b> A1C data comes from DrChrono /vitals or is text-parsed from synced clinical notes. If both return 0 readings, A1C isn't recorded structurally in DrChrono.
          </div>
        </>
      )}
    </div>
  );
}

function PatientCard({ p }) {
  const has = p.readings?.length > 0;
  const isImproving = p.delta != null && p.delta < -0.3;
  const isWorsening = p.delta != null && p.delta > 0.3;
  const deltaColor = isImproving ? "text-[#3B5C46]" : isWorsening ? "text-[#8A3E30]" : "text-[#8A847A]";
  const DeltaIcon = isImproving ? TrendingDown : isWorsening ? TrendingUp : Minus;

  return (
    <div className="bg-white border border-[#E5E0D8] rounded-md p-5" data-testid={`a1c-patient-${p.drchrono_patient_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.08em] text-[#8A847A]">Under care of {p.np_name || "—"}</div>
          <h3 className="heading text-lg font-semibold text-[#292723] mt-0.5">{p.patient_name}</h3>
          <div className="text-[11px] text-[#8A847A] mt-0.5">{p.visit_count} visits · Chart {p.drchrono_patient_id}</div>
        </div>
        {has && p.delta != null && (
          <div className={`flex items-center gap-1.5 ${deltaColor}`}>
            <DeltaIcon className="h-4 w-4" />
            <span className="font-mono font-semibold text-lg">{p.delta > 0 ? "+" : ""}{p.delta.toFixed(1)}</span>
          </div>
        )}
      </div>

      {has ? (
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div className="p-3 rounded bg-[#F7F5F0] border border-[#E5E0D8]">
            <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A847A]">First reading</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="heading text-2xl font-bold text-[#292723] font-mono">{p.first.value.toFixed(1)}</span>
              <span className="text-[10px] text-[#8A847A]">%</span>
            </div>
            <div className="text-[10px] text-[#8A847A] mt-1">{p.first.date}</div>
          </div>
          <div className="p-3 rounded bg-[#F7F5F0] border border-[#E5E0D8]">
            <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A847A]">Latest reading</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className={`heading text-2xl font-bold font-mono ${p.latest.value >= 9 ? "text-[#8A3E30]" : p.latest.value >= 7 ? "text-[#B08A55]" : "text-[#3B5C46]"}`}>
                {p.latest.value.toFixed(1)}
              </span>
              <span className="text-[10px] text-[#8A847A]">%</span>
            </div>
            <div className="text-[10px] text-[#8A847A] mt-1">{p.latest.date}</div>
          </div>
        </div>
      ) : (
        <div className="mt-3 text-xs text-[#8A6E2A] p-2 bg-[#FEF7E6] border border-[#E5D9BE] rounded">
          <Activity className="h-3 w-3 inline mr-1"/> No A1C readings found in DrChrono for this patient
          {p.endpoint_status && (
            <span className="ml-1 font-mono">
              (/vitals: HTTP {p.endpoint_status.vitals || "?"})
            </span>
          )}
        </div>
      )}

      {has && p.readings.length > 2 && (
        <div className="mt-3 text-[10px] text-[#8A847A]">
          All readings: {p.readings.map(r => `${r.value.toFixed(1)} (${r.date})`).join(" → ")}
          {p.source && <span className="ml-2 italic">· source: {p.source}</span>}
        </div>
      )}
    </div>
  );
}
